<template>
    <div class="container">
        <div class="row justify-content-center">
            <div v-if="registered" class="col text-center">
                <div class="row justify-content-center">
                    <h5 class="text-white mt-5">
                        {{ $t('message.lastStepToRegister') }}
                    </h5>
                </div>

                <div class="row justify-content-center">
                    <h6 class="text-white">
                        {{ `${$t('message.checkActivationMail')}&nbsp;` }}<strong>{{ mail }}</strong>
                        <br>

                        <div class="d-flex justify-content-center">
                            {{ $t('message.expireDateWarning') }}
                        </div>
                    </h6>
                </div>
            </div>

            <div v-else class="form-container">
                <div class="card bg-transparent border-0">
                    <div class="card-body pt-0 pb-0">
                        <b-tabs
                            v-model="tabIndex"
                            active-nav-item-class="active-tab-class"
                            nav-class="title-nav-class"
                            no-nav-style
                            no-fade
                            @activate-tab="emptyInput"
                        >
                            <b-tab :title="$t('title.login')" title-link-class="tab-class">
                                <vk-login v-if="features.vkAuth" usage="login" :link="vkLink" />
                                
                                <ValidationObserver ref="loginObserver">
                                    <form id="login" class="mt-3">
                                        <v-input
                                            v-model="login"
                                            class="input-w-default mb-0 mx-auto"
                                            ind="0"
                                            type="text"
                                            :placeholder="$t('placeholder.email')"
                                            :icon="$getInputIcon(login, 'email')"
                                            rules="required"
                                            autocomplete="username"
                                            autofocus
                                            @input="clearError"
                                            @keyEnterUp="signIn"
                                        />

                                        <v-input
                                            v-model="password"
                                            class="input-w-default mb-0 mx-auto mt-2"
                                            ind="1"
                                            :type="openPasswordLogin ? 'text' : 'password'"
                                            :placeholder="$t('placeholder.password')"
                                            :icon="$getInputIcon(password, 'password')"
                                            rules="required"
                                            autocomplete="current-password"
                                            @input="clearError"
                                            @keyEnterUp="signIn"
                                        >
                                            <template #password>
                                                <password-eye v-model="openPasswordLogin" />
                                            </template>
                                        </v-input>

                                        <b-form-invalid-feedback v-if="error !== ''" class="center-element" style="width: 90%">
                                            {{ error }}
                                        </b-form-invalid-feedback>

                                        <b-button class="center-element enter-button w-75 mb-2" @click="signIn">
                                            {{ $t('button.login') }}
                                        </b-button>
                                        
                                        <a class="center-element small text-link mt-2" @click="restore" style="width: fit-content; cursor: pointer;">
                                            {{ $t('message.forgotPassword') }}
                                        </a>
                                    </form>
                                </ValidationObserver>
                            </b-tab>

                            <b-tab :title="$t('title.register')" title-link-class="tab-class">
                                <ValidationObserver ref="registerObserver">
                                    <form id="registerStudent">
                                        <vk-login v-if="features.vkAuth" usage="register" :link="vkLink" />
                                        
                                        <v-input
                                            v-model="surname"
                                            class="input-w-default mt-3 mb-0 mx-auto"
                                            ind="2"
                                            type="text"
                                            :placeholder="$t('placeholder.surname')"
                                            :icon="$getInputIcon(surname, 'name')"
                                            rules="required"
                                        />

                                        <v-input
                                            v-model="name"
                                            class="input-w-default mb-0 mx-auto mt-2"
                                            ind="3"
                                            type="text"
                                            :placeholder="$t('placeholder.name')"
                                            :icon="$getInputIcon(name, 'name')"
                                            rules="required"
                                        />

                                        <v-input
                                            v-model="mail"
                                            class="input-w-default mb-0 mx-auto mt-2"
                                            ind="4"
                                            type="text"
                                            :placeholder="$t('placeholder.email')"
                                            :icon="$getInputIcon(mail, 'email')"
                                            :debounce="500"
                                            rules="required|email|emailNotExists"
                                            autocomplete="username"
                                        />

                                        <v-input
                                            v-model="password"
                                            class="input-w-default mb-0 mx-auto mt-2"
                                            ind="5"
                                            type="password"
                                            name="password"
                                            :placeholder="$t('placeholder.password')"
                                            :icon="$getInputIcon(password, 'password')"
                                            :rules="{required: true, min: 6}"
                                            autocomplete="new-password"
                                        />

                                        <v-input
                                            v-model="passwordConfirm"
                                            class="input-w-default mx-auto mt-2"
                                            ind="6"
                                            type="password"
                                            name="passwordConfirm"
                                            :placeholder="$t('placeholder.repeatPassword')"
                                            :icon="$getInputIcon(passwordConfirm, 'password')"
                                            rules="required|confirmed:password"
                                            autocomplete="new-password"
                                        />
                                    </form>

                                    <ValidationProvider v-slot="{ errors }" :rules="{isTrue: true}" slim>
                                        <b-form-checkbox id="confirmUserAgreementAndPrivacyPolicy" v-model="confirmUserAgreementAndPrivacyPolicy" class="text-white small mx-auto agreements-checkbox">
                                            {{ $t('message.acceptTerms') }}
                                            
                                            <br>

                                            <router-link class="text-link" to="/user-agreement" target="_blank">
                                                {{ $t('title.userAgreementGenitive') }}
                                            </router-link>
                                            
                                            {{ `&nbsp;${$t('message.and')}\n` }}
                                            
                                            <br>
                                            
                                            <router-link class="text-link" to="/privacy-policy" target="_blank">
                                                {{ $t('title.privacyPolicyGenitive') }}
                                            </router-link>
                                        </b-form-checkbox>

                                        <b-popover v-if="errors.length" target="confirmUserAgreementAndPrivacyPolicy" placement="left" variant="danger" show>
                                            {{ $t('message.confirmTerms') }}
                                        </b-popover>
                                    </ValidationProvider>
                                    
                                    <ValidationProvider v-slot="{ errors }" :rules="{isTrue: true}" slim>
                                        <b-form-checkbox id="confirmPersonalDataAgreement" v-model="confirmPersonalDataAgreement" class="text-white small mx-auto agreements-checkbox">
                                            {{ $t('message.acceptPersonalDataAgreement') }}
                                            
                                            <router-link class="text-link" to="/personal-data-agreement" target="_blank">
                                                {{ $t('title.personalDataAgreement') }}
                                            </router-link>
                                        </b-form-checkbox>

                                        <b-popover v-if="errors.length" target="confirmPersonalDataAgreement" placement="left" variant="danger" show>
                                            {{ $t('message.confirmTerms') }}
                                        </b-popover>
                                    </ValidationProvider>
                                </ValidationObserver>

                                <b-form-checkbox id="acceptedMailing" v-model="acceptedMailing" class="text-white small mx-auto mt-4 input-w-default">
                                    {{ `${$t('message.acceptedMailing')}&nbsp;` }}
                                </b-form-checkbox>

                                <b-form-invalid-feedback v-if="error !== ''" class="center-element">
                                    {{ error }}
                                </b-form-invalid-feedback>

                                <b-button :disabled="!confirmPersonalDataAgreement || !confirmUserAgreementAndPrivacyPolicy" class="center-element enter-button px-5" @click="register">
                                    {{ $t('button.register') }}
                                </b-button>
                            </b-tab>
                        </b-tabs>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import api from '../api';
    import VInput from './VInput';
    import PasswordEye from './PasswordEye.vue';
    import UrlUtils from './mixins/UrlUtils';
    import { USER_SET_STATUS } from 'mutations/user';
    import VkLogin from './VkLogin.vue';
    import Queries from './mixins/Queries';
    import { mapGetters } from 'vuex';

    export default {
        name: 'Login',

        components: {
            VkLogin,
            VInput,
            PasswordEye
        },

        mixins: [
            UrlUtils,
            Queries
        ],

        data() {
            return {
                login: '',
                vkRedirect: '',
                vkLink: '',
                flow: '',
                redirectUri: '',
                name: '',
                surname: '',
                mail: '',
                password: '',
                passwordConfirm: '',
                openPasswordLogin: false,
                confirmUserAgreementAndPrivacyPolicy: false,
                confirmPersonalDataAgreement: false,
                acceptedMailing: false,
                error: '',
                registered: false,
                tabIndex: 0
            };
        },
        
        computed: {
            ...mapGetters([ 'features' ])
        },

        async created() {
            const FLOW_TYPES = this.$defines.FLOW_TYPES;
            let query = this.$route.query;

            this.redirectUri = query.redirect_uri || query.redirect || '/account';
            this.flow = query.redirect_uri ? FLOW_TYPES.OAUTH : FLOW_TYPES.INTERNAL;

            this.vkLink = `/app/authorize/vk?flow=${this.flow}&redirect_uri=${this.redirectUri}`;

            if (this.flow === this.$defines.FLOW_TYPES.OAUTH) {
                let queryString = `&state=${query.state}&code_challenge=${query.code_challenge}&client_id=${query.client_id}`;

                this.vkLink += queryString;
            }
        },

        methods: {
            restore() {
                this.emptyInput();

                this.openRelativeUrl('/restore/request', false);
            },

            async signIn() {
                this.openPasswordLogin = false;
                let isValid = await this.$refs.loginObserver.validate();

                if (!isValid)
                    return;

                const FLOW_TYPES = this.$defines.FLOW_TYPES;

                this.error = '';

                try {
                    let body = {
                        login: this.login,
                        password: this.password,
                        flow: this.flow,
                        // eslint-disable-next-line camelcase
                        redirect_uri: this.redirectUri
                    };

                    if (this.flow === FLOW_TYPES.OAUTH) {
                        body.state = this.$route.query.state;
                        // eslint-disable-next-line camelcase
                        body.code_challenge = this.$route.query.code_challenge;
                        // eslint-disable-next-line camelcase
                        body.client_id = this.$route.query.client_id;
                    }

                    let response = await api.post('/app/authorize', body);

                    if (response && response.status === this.$defines.RESPONSE_STATUS.OK)
                        this.$store.commit(`user/${USER_SET_STATUS}`, this.$defines.AUTHORIZE_STATUS.ACCOUNT_EXISTS);

                    if (this.flow === this.$defines.FLOW_TYPES.OAUTH)
                        this.openUrl(response.data.redirect, false, false);
                    else
                        await this.openRelativeUrl(this.$route.query.redirect || '/account', false);
                }
                catch (error) {
                    if (error.response.status === this.$defines.RESPONSE_STATUS.UNAUTHORIZED) {
                        this.$store.commit(`user/${USER_SET_STATUS}`, this.$defines.AUTHORIZE_STATUS.UNAUTHORIZED);

                        let ERROR_STATUS = this.$defines.ERROR_STATUS;

                        switch (error.response.data.type) {
                            case ERROR_STATUS.ACCOUNT_NOT_FOUND:
                            case ERROR_STATUS.ACCOUNT_REMOVED:
                                this.error = this.$t('error.accountNotExists');
                                break;
                            case ERROR_STATUS.INCORRECT_PASSWORD:
                                this.error = this.$t('error.wrongPassword');
                                break;
                            case ERROR_STATUS.ACCOUNT_UNACTIVATED:
                                this.error = this.$t('error.accountNotActivated');
                                break;
                            case ERROR_STATUS.PASSWORD_NOT_EXISTS:
                                this.error = this.$t('error.passwordNotExists');
                                break;
                        }
                    }
                }
            },

            async register() {
                let isValid = await this.$refs.registerObserver.validate();

                if (isValid) {
                    try {
                        await api.post('/app/registration/register', {
                            type: this.$defines.SERVICE_ACCOUNT_TYPES.USER,
                            name: this.name,
                            surname: this.surname,
                            mail: this.mail,
                            password: this.password,
                            acceptedMailing: this.acceptedMailing
                        });

                        this.registered = true;
                    }
                    catch (e) {
                        this.registered = false;
                        this.error = this.$t('error.registerFailed');
                    }
                }
            },

            clearError() {
                this.error = '';
            },

            emptyInput(index) {
                this.tabIndex = index;

                this.$refs.loginObserver.reset();
                this.$refs.registerObserver.reset();
                this.error = '';

                this.login = '';
                this.password = '';
                this.passwordConfirm = '';
                this.surname = '';
                this.name = '';
                this.mail = '';
            }
        }
    };
</script>

<style>
    .title-nav-class {
        justify-content: center;
    }

    .tab-class {
        outline: none;
        color: white;
        font-size: 21px;
        font-weight: bold;
        opacity: 0.6;
    }

    .tab-class:active {
        outline: none;
    }

    .tab-class:hover {
        color: #29ABE2;
    }

    .active-tab-class {
        text-decoration: underline !important;
        text-decoration-skip-ink: none;
        opacity: 1;
    }
</style>

<style scoped>
    .enter-button {
        width: auto;
        background-color: transparent !important;
        border: 1px solid #36AB71 !important;
        border-radius: 5px !important;
        margin-top: 24px !important;
    }

    .enter-button:hover:not(:disabled) {
        background-color: #36AB71 !important;
    }

    .center-element {
        display: flex !important;
        flex-direction: row !important;
        justify-content: center !important;
        margin-left: auto !important;
        margin-right: auto !important;
    }

    .text-link {
        color: #29ABE2 !important;
    }

    .agreements-checkbox {
        margin: 28px 0.5rem auto;
        width: fit-content;
        max-width: 304px;
        min-width: 304px;
    }

    /deep/ .custom-control-label::before, /deep/ .custom-control-label::after {
        top: 0.125rem;
    }
</style>
